import { useEffect, useMemo, useState } from "react";

import {
  TranscriptionStatus,
  useGetTranscriptionStatusesQuery,
} from "../app/services/mediaTranscription";

/**
 * 一覧に並んだメディアの文字起こしステータスをまとめて購読する。
 *
 * 一覧（共有ライブラリ / トークルーム / メッセンジャー / ショット編集 /
 * 作品登録）のステータスチップ / 本文プレビュー用。実行中のファイルがある間
 * だけポーリングし、リロードせずに「文字起こし中」→ 完了表示へ切り替わる
 * ようにする（`MediaTranscriptionModal` の単体ポーリングと同じ「条件付き
 * pollingInterval」パターン）。
 *
 * 単体の実行 / 削除も同じ `Transcription` タグを無効化するので、
 * モーダルから実行した結果が一覧に自動で反映される。
 *
 * 戻り値は fileId -> { status, textPreview } の辞書。レコードが無いファイル
 * （= 未実行）は含まれないので、表示側は `undefined` を非表示として扱う。
 */

/** 一覧セルに表示する 1 ファイルぶんの文字起こし情報 */
export interface TranscriptionCellInfo {
  status: TranscriptionStatus;
  /**
   * complete のときの本文プレビュー（先頭部分を 1 行に畳んだもの。
   * タイムスタンプなし）。complete 以外や旧データでは undefined。
   */
  textPreview?: string;
}

/**
 * complete かつ本文プレビューがある（= セルにチップではなく本文を出す）か。
 *
 * 呼び出し側はこれが true のとき、ファイル名側の `flex: 1`（伸長）を外して
 * 「名前の直後からプレビューが続き、残り幅を使い切る」レイアウトにする
 * （共有ライブラリのファイル名セルと同じ見え方に統一するため）。
 */
export const hasTranscriptionPreview = (
  entry?: TranscriptionCellInfo,
): entry is TranscriptionCellInfo & { textPreview: string } =>
  entry?.status === "complete" && !!entry.textPreview;

/** 実行中のファイルがある間のポーリング間隔（ms） */
const ACTIVE_POLLING_INTERVAL = 15_000;

/** 1 リクエストに載せられる file_id の上限（BE の MAX_STATUS_FILE_IDS と揃える） */
const MAX_FILE_IDS = 200;

const useTranscriptionStatuses = (
  fileIds: string[],
  skip = false,
): Record<string, TranscriptionCellInfo> => {
  // 呼び出し側は毎レンダー新しい配列を渡してくることが多い。そのままだと
  // キャッシュキーが安定せず購読が張り直され続けるので、内容ベースで
  // メモ化した配列を渡す。
  // 上限を超えるぶんは渡された順（= 一覧の表示順）で切り、先頭の画面に
  // 出ている行からチップが付くようにする。
  const cacheKey = useMemo(() => {
    const unique = Array.from(new Set(fileIds.filter(Boolean))).slice(
      0,
      MAX_FILE_IDS,
    );
    // 並びが違うだけの同じ集合でキャッシュを分けないよう、キーはソートする
    return unique.sort().join(",");
  }, [fileIds]);

  const queryArg = useMemo(
    () => (cacheKey ? cacheKey.split(",") : []),
    [cacheKey],
  );

  const [pollingInterval, setPollingInterval] = useState(0);

  const { data } = useGetTranscriptionStatusesQuery(queryArg, {
    skip: skip || queryArg.length === 0,
    pollingInterval,
  });

  const entries = data?.data?.statuses;

  const statuses = useMemo(() => {
    const map: Record<string, TranscriptionCellInfo> = {};
    entries?.forEach((entry) => {
      map[entry.file_id] = {
        status: entry.transcription_status,
        textPreview: entry.text_preview || undefined,
      };
    });
    return map;
  }, [entries]);

  const hasActive = useMemo(
    () =>
      Object.values(statuses).some(
        (info) => info.status === "pending" || info.status === "processing",
      ),
    [statuses],
  );

  // 全て確定したらポーリングを止める（一覧を開いているだけで叩き続けない）。
  useEffect(() => {
    setPollingInterval(hasActive ? ACTIVE_POLLING_INTERVAL : 0);
  }, [hasActive]);

  return statuses;
};

export default useTranscriptionStatuses;
